<p-confirmdialog />

<!--
  Category total entered directly, with no breakdown (how the client app records
  a category when the member skips itemizing). Hidden once itemized amounts exist,
  since those take precedence for the stored total.
-->
<ng-template #totalField let-catKey="catKey">
  @if (isItemized(catKey)) {
    <p class="bn-fs-edit__total-note">
      Total is calculated from the amounts below. Clear them to enter a total directly.
    </p>
  } @else {
    <label class="bn-fs-edit__field bn-fs-edit__total-row" [for]="'bn-fs-total-' + catKey">
      <span>Total (no breakdown)</span>
      <p-inputnumber
        [inputId]="'bn-fs-total-' + catKey"
        [ngModel]="getNum(categoryTotalFormKey(catKey))"
        (ngModelChange)="setNum(categoryTotalFormKey(catKey), $event)"
        mode="currency"
        currency="USD"
        locale="en-US"
        [min]="0"
        [minFractionDigits]="0"
        [maxFractionDigits]="0"
        [showClear]="true"
        [invalid]="hasFieldError(categoryTotalFormKey(catKey))"
        styleClass="bn-fs-edit__input"
        inputStyleClass="bn-fs-edit__input"
      />
    </label>
  }
</ng-template>

<div class="bn-fs-edit" role="form" aria-labelledby="bn-fs-edit-title">
  <header class="bn-fs-edit__header">
    <h2 id="bn-fs-edit-title" class="bn-fs-edit__title">Edit financial snapshot</h2>
    <button
      type="button"
      class="bn-fs-edit__close"
      aria-label="Close"
      [disabled]="saving()"
      (click)="tryClose()"
    >
      <i class="pi pi-times" aria-hidden="true"></i>
    </button>
  </header>

  @if (clearedCategoriesMessage(); as clearedMessage) {
    <p class="bn-fs-edit__error" role="alert">{{ clearedMessage }}</p>
  }
  @if (amountValidationHint()) {
    <p class="bn-fs-edit__error" role="status">{{ amountValidationHint() }}</p>
  }
  @if (saveError()) {
    <p class="bn-fs-edit__error" role="alert">{{ saveError() }}</p>
  }

  <div class="bn-fs-edit__grid">
    <section class="bn-fs-edit__col" aria-labelledby="bn-fs-edit-income-heading">
      <h3 id="bn-fs-edit-income-heading" class="bn-fs-edit__col-title">Income &amp; cashflow</h3>

      <div class="bn-fs-edit__cat" [class.bn-fs-edit__cat--error]="categoryHasError(incomeKey)">
        <div class="bn-fs-edit__cat-row">
          <button
            type="button"
            class="bn-fs-edit__cat-header"
            [attr.aria-expanded]="isExpanded(incomeKey)"
            (click)="toggleExpanded(incomeKey)"
          >
            <i
              class="pi"
              [class.pi-chevron-up]="isExpanded(incomeKey)"
              [class.pi-chevron-right]="!isExpanded(incomeKey)"
              aria-hidden="true"
            ></i>
            <span>Household income</span>
            <span class="bn-fs-edit__sum-wrap">
              <span class="bn-fs-edit__sum-box">
                @if (categorySum(incomeKey) !== null) {
                  {{ categorySum(incomeKey)! | currency: 'USD' : 'symbol' : '1.0-0' }}
                } @else {
                  —
                }
              </span>
              <span class="bn-fs-edit__sum-badge">
                {{ isItemized(incomeKey) ? 'SUM' : 'TOTAL' }}
              </span>
            </span>
          </button>
          @if (categoryInstructions(incomeKey); as tip) {
            <button
              type="button"
              class="bn-fs-edit__tip"
              [pTooltip]="tip"
              tooltipPosition="top"
              [attr.aria-label]="'About Household income'"
            >
              <i class="pi pi-info-circle" aria-hidden="true"></i>
            </button>
          }
        </div>
        @if (isExpanded(incomeKey)) {
          <div class="bn-fs-edit__subs">
            <ng-container
              *ngTemplateOutlet="totalField; context: { catKey: incomeKey }"
            />
            @for (sub of incomeSubs(); track sub.formKey) {
              <label class="bn-fs-edit__field" [for]="'bn-fs-' + sub.formKey">
                <span>{{ sub.label }}</span>
                <p-inputnumber
                  [inputId]="'bn-fs-' + sub.formKey"
                  [ngModel]="getNum(sub.formKey)"
                  (ngModelChange)="setNum(sub.formKey, $event)"
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  [min]="0"
                  [minFractionDigits]="0"
                  [maxFractionDigits]="0"
                  [showClear]="true"
                  [invalid]="hasFieldError(sub.formKey)"
                  styleClass="bn-fs-edit__input"
                  inputStyleClass="bn-fs-edit__input"
                />
              </label>
            }
          </div>
        }
      </div>

      <dl class="bn-fs-edit__preview">
        <div class="bn-fs-edit__preview-row">
          <dt>Monthly costs</dt>
          <dd>
            @if (preview().metrics.monthlyCosts === null) {
              —
            } @else {
              {{ preview().metrics.monthlyCosts | currency: 'USD' : 'symbol' : '1.0-0' }}
            }
          </dd>
        </div>
        <div
          class="bn-fs-edit__preview-row"
          [class.bn-fs-edit__preview-row--shortfall]="preview().metrics.netCashflowStatus === 'shortfall'"
        >
          <dt>Net cashflow</dt>
          <dd>
            @if (preview().metrics.netCashflow === null) {
              —
            } @else {
              {{ preview().metrics.netCashflow | currency: 'USD' : 'symbol' : '1.0-0' }}
            }
          </dd>
        </div>
      </dl>

      <p class="bn-fs-edit__progress" aria-live="polite">
        {{ enteredCount() }} of {{ totalUiRows }} rows entered
      </p>
    </section>

    <section class="bn-fs-edit__col" aria-labelledby="bn-fs-edit-spend-heading">
      <h3 id="bn-fs-edit-spend-heading" class="bn-fs-edit__col-title">Spending by category</h3>
      <p class="bn-fs-edit__hint" role="note">
        <i class="pi pi-info-circle" aria-hidden="true"></i>
        <span>If a category has no spending, enter <strong>$0</strong> for the total — leaving it blank means “not asked yet.”</span>
      </p>

      @for (catKey of spendingKeys; track catKey) {
        <div class="bn-fs-edit__cat" [class.bn-fs-edit__cat--error]="categoryHasError(catKey)">
          <div class="bn-fs-edit__cat-row">
            <button
              type="button"
              class="bn-fs-edit__cat-header"
              [attr.aria-expanded]="isExpanded(catKey)"
              (click)="toggleExpanded(catKey)"
            >
              <i
                class="pi"
                [class.pi-chevron-up]="isExpanded(catKey)"
                [class.pi-chevron-right]="!isExpanded(catKey)"
                aria-hidden="true"
              ></i>
              <span>{{ displayCategoryName(catKey) }}</span>
              <span class="bn-fs-edit__sum-wrap">
                <span class="bn-fs-edit__sum-box">
                  @if (categorySum(catKey) !== null) {
                    {{ categorySum(catKey)! | currency: 'USD' : 'symbol' : '1.0-0' }}
                  } @else {
                    —
                  }
                </span>
                <span class="bn-fs-edit__sum-badge">
                  {{ isItemized(catKey) ? 'SUM' : 'TOTAL' }}
                </span>
                <span class="bn-fs-edit__pct">
                  @if (percentOfIncome(categorySum(catKey)); as pct) {
                    {{ pct }}%
                  }
                </span>
              </span>
            </button>
            @if (categoryInstructions(catKey); as tip) {
              <button
                type="button"
                class="bn-fs-edit__tip"
                [pTooltip]="tip"
                tooltipPosition="top"
                [attr.aria-label]="'About ' + displayCategoryName(catKey)"
              >
                <i class="pi pi-info-circle" aria-hidden="true"></i>
              </button>
            }
          </div>
          @if (isExpanded(catKey)) {
            <div class="bn-fs-edit__subs">
              <ng-container *ngTemplateOutlet="totalField; context: { catKey: catKey }" />
              @for (sub of spendingSubs(catKey); track sub.formKey) {
                <label class="bn-fs-edit__field" [for]="'bn-fs-' + sub.formKey">
                  <span>{{ sub.label }}</span>
                  <p-inputnumber
                    [inputId]="'bn-fs-' + sub.formKey"
                    [ngModel]="getNum(sub.formKey)"
                    (ngModelChange)="setNum(sub.formKey, $event)"
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    [min]="0"
                    [minFractionDigits]="0"
                    [maxFractionDigits]="0"
                    [showClear]="true"
                    [invalid]="hasFieldError(sub.formKey)"
                    styleClass="bn-fs-edit__input"
                    inputStyleClass="bn-fs-edit__input"
                  />
                </label>
              }
            </div>
          }
        </div>
      }

      <div class="bn-fs-edit__cat" [class.bn-fs-edit__cat--error]="categoryHasError(otherKey)">
        <div class="bn-fs-edit__cat-row">
          <button
            type="button"
            class="bn-fs-edit__cat-header"
            [attr.aria-expanded]="isExpanded(otherKey)"
            (click)="toggleExpanded(otherKey)"
          >
            <i
              class="pi"
              [class.pi-chevron-up]="isExpanded(otherKey)"
              [class.pi-chevron-right]="!isExpanded(otherKey)"
              aria-hidden="true"
            ></i>
            <span>{{ displayCategoryName(otherKey) }}</span>
            <span class="bn-fs-edit__sum-wrap">
              <span class="bn-fs-edit__sum-box">
                @if (categorySum(otherKey) !== null) {
                  {{ categorySum(otherKey)! | currency: 'USD' : 'symbol' : '1.0-0' }}
                } @else {
                  —
                }
              </span>
              <span class="bn-fs-edit__sum-badge">
                {{ isItemized(otherKey) ? 'SUM' : 'TOTAL' }}
              </span>
              <span class="bn-fs-edit__pct">
                @if (percentOfIncome(categorySum(otherKey)); as pct) {
                  {{ pct }}%
                }
              </span>
            </span>
          </button>
          @if (categoryInstructions(otherKey); as tip) {
            <button
              type="button"
              class="bn-fs-edit__tip"
              [pTooltip]="tip"
              tooltipPosition="top"
              aria-label="About Other"
            >
              <i class="pi pi-info-circle" aria-hidden="true"></i>
            </button>
          }
        </div>
        @if (isExpanded(otherKey)) {
          <div class="bn-fs-edit__subs">
            <ng-container *ngTemplateOutlet="totalField; context: { catKey: otherKey }" />
            @for (row of otherNamed; track row.key) {
              <label class="bn-fs-edit__field" [for]="'bn-fs-' + otherNamedFormKey(row.key)">
                <span>{{ row.shortLabel }}</span>
                <p-inputnumber
                  [inputId]="'bn-fs-' + otherNamedFormKey(row.key)"
                  [ngModel]="getNum(otherNamedFormKey(row.key))"
                  (ngModelChange)="setNum(otherNamedFormKey(row.key), $event)"
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  [min]="0"
                  [minFractionDigits]="0"
                  [maxFractionDigits]="0"
                  [showClear]="true"
                  [invalid]="hasFieldError(otherNamedFormKey(row.key))"
                  styleClass="bn-fs-edit__input"
                  inputStyleClass="bn-fs-edit__input"
                />
              </label>
            }

            @for (custom of otherCustoms(); track custom.key) {
              <div class="bn-fs-edit__custom">
                <div class="bn-fs-edit__custom-head">
                  <span class="bn-fs-edit__custom-title">Custom category</span>
                  <button
                    type="button"
                    class="bn-fs-edit__custom-remove"
                    [attr.aria-label]="'Remove ' + (custom.label || 'custom category')"
                    (click)="removeOtherCustom(custom.key)"
                  >
                    <i class="pi pi-trash" aria-hidden="true"></i>
                  </button>
                </div>
                <label class="bn-fs-edit__field bn-fs-edit__field--stack" [for]="'bn-fs-other-custom-label-' + custom.key">
                  <span>Label</span>
                  <input
                    pInputText
                    type="text"
                    class="bn-fs-edit__label-input"
                    [id]="'bn-fs-other-custom-label-' + custom.key"
                    [ngModel]="custom.label"
                    (ngModelChange)="setOtherCustomLabel(custom.key, $event)"
                    (blur)="commitOtherCustomLabel(custom.key)"
                    [attr.maxlength]="otherCustomLabelMaxLen"
                    placeholder="e.g. Donations, gifts, etc."
                  />
                </label>
                <label class="bn-fs-edit__field" [for]="'bn-fs-other-custom-amt-' + custom.key">
                  <span>Monthly amount</span>
                  <p-inputnumber
                    [inputId]="'bn-fs-other-custom-amt-' + custom.key"
                    [ngModel]="getOtherCustomValue(custom.key)"
                    (ngModelChange)="setOtherCustomValue(custom.key, $event)"
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    [min]="0"
                    [minFractionDigits]="0"
                    [maxFractionDigits]="0"
                    [showClear]="true"
                    [invalid]="hasFieldError(otherCustomFormKey(custom.key))"
                    styleClass="bn-fs-edit__input"
                    inputStyleClass="bn-fs-edit__input"
                  />
                </label>
              </div>
            }

            <button type="button" class="bn-fs-edit__add-custom" (click)="addOtherCustom()">
              Add another category
            </button>
          </div>
        }
      </div>

      <label class="bn-fs-edit__field bn-fs-edit__field--row bn-fs-edit__savings" for="bn-fs-savings">
        <span class="bn-fs-edit__row-label">
          Savings
          @if (categoryInstructions('savings'); as tip) {
            <button
              type="button"
              class="bn-fs-edit__tip"
              [pTooltip]="tip"
              tooltipPosition="top"
              aria-label="About Savings"
            >
              <i class="pi pi-info-circle" aria-hidden="true"></i>
            </button>
          }
        </span>
        <span class="bn-fs-edit__field-end">
          <p-inputnumber
            inputId="bn-fs-savings"
            [ngModel]="getNum(savingsFormKey)"
            (ngModelChange)="setNum(savingsFormKey, $event)"
            mode="currency"
            currency="USD"
            locale="en-US"
            [min]="0"
            [minFractionDigits]="0"
            [maxFractionDigits]="0"
            [showClear]="true"
            [invalid]="hasFieldError(savingsFormKey)"
            styleClass="bn-fs-edit__input"
            inputStyleClass="bn-fs-edit__input"
          />
          <span class="bn-fs-edit__pct">
            @if (percentOfIncome(getNum(savingsFormKey)); as pct) {
              {{ pct }}%
            }
          </span>
        </span>
      </label>
    </section>
  </div>

  <footer class="bn-fs-edit__footer">
    <p-button
      label="Cancel"
      severity="secondary"
      [outlined]="true"
      [disabled]="saving()"
      (onClick)="tryClose()"
    />
    @if (isSubmitted) {
      <p-button
        label="Save"
        icon="pi pi-check"
        [loading]="saving()"
        [disabled]="saving() || !dirty() || sessionMismatch()"
        (onClick)="saveProgress()"
      />
    } @else if (canSubmit()) {
      <p-button
        label="Submit"
        icon="pi pi-check"
        [loading]="saving()"
        [disabled]="saving() || sessionMismatch()"
        (onClick)="submitSnapshot()"
      />
    } @else {
      <p-button
        label="Save progress"
        icon="pi pi-check"
        [loading]="saving()"
        [disabled]="saving() || !dirty() || sessionMismatch()"
        (onClick)="saveProgress()"
      />
    }
  </footer>
</div>
